import React, { useCallback, useContext, useState } from 'react';

import { ToastContext } from '@pflegia/ui/contexts/toast.context';
import { uuidV4 } from '@pflegia/util/common/uuid-v4';

import ToastContainer from './ToastContainer';

export const TOAST_DEFAULT_LIFETIME = 10000;
export const TOAST_SHORT_LIFETIME = 5000;

export type ToastProviderProps = {
  children: React.ReactNode;
};

export enum ToastMessageType {
  Info = 'Info',
  Success = 'Success',
  Warning = 'Warning',
  Error = 'Error',
}

export type Toast = {
  id: string;
  lifetime: number;
  message: string;
  type?: ToastMessageType;
};

/**
 * @deprecated import from @pflegia/ui/contexts/toast.context instead
 */
export const useToast = () => useContext(ToastContext);

const DEFAULT_INTERVAL = 4000;

const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toastList, setToastList] = useState<Toast[]>([]);

  const pushToastMessage = useCallback(
    (message: string, type: ToastMessageType, lifetime?: number) => {
      if (message) {
        const newItem: Toast = {
          id: uuidV4(),
          message,
          type,
          lifetime: lifetime ? lifetime : DEFAULT_INTERVAL,
        };

        setToastList(prevState => prevState.concat(newItem));
      }
    },
    [setToastList],
  );

  const addError = useCallback(
    (message: string, lifetime?: number) => pushToastMessage(message, ToastMessageType.Error, lifetime),
    [pushToastMessage],
  );
  const addWarning = useCallback(
    (message: string, lifetime?: number) => pushToastMessage(message, ToastMessageType.Warning, lifetime),
    [pushToastMessage],
  );
  const addSuccess = useCallback(
    (message: string, lifetime?: number) => pushToastMessage(message, ToastMessageType.Success, lifetime),
    [pushToastMessage],
  );
  const addInfo = useCallback(
    (message: string, lifetime?: number) => pushToastMessage(message, ToastMessageType.Info, lifetime),
    [pushToastMessage],
  );

  const contextValue = React.useMemo(
    () => ({
      error: addError,
      warning: addWarning,
      success: addSuccess,
      info: addInfo,
      remove(id: string) {
        setToastList(currentToasts => currentToasts.filter(toast => toast.id !== id));
      },
    }),
    [setToastList, addError, addWarning, addSuccess, addInfo],
  );

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
      <ToastContainer list={toastList} />
    </ToastContext.Provider>
  );
};

export default ToastProvider;
